import { StyleService } from "@/store/style/style-service"
import { CatalogManager } from "."
import { ImageInfoRecord, StyleInfoRecord, StyleReferenceRecord } from "./types"
import { store } from "@/store/store"
import {
	currentStyleAtom,
	ProjectStyleTypes,
	RemoteStyle,
	saveAsCurrentStyle,
	StyleDraft,
	styleReferencesAtom,
} from "@/store/style/style-state"
import { PublishStyleDraftResult, publishStyleDraft } from "@/store/style/publish-style"
import { photosAtom } from "@/store/photo/photos-state"
import { removeFromApproved } from "@/store/style/reference"
import { updateAIStyle } from "@/store/style/update-style"
import { isDuplicateStyleNameError } from "@/store/style/style-service"
import { showDuplicateStyleNameRecoveryDialog } from "@/store/style/duplicate-name-recovery"
import { DialogProps } from "@/components/ui/Dialog"
import { MainLUTResolver, deserializeLookTable } from "@/io/assets/lut/lut-resolver"
import { storeStyleLut, getStyleLut } from "@/storage/indexeddb"
import {
	toLocalPhotoIdFromStyleReferenceImageId,
	toRemoteStyleReferenceImageId,
} from "@/store/style/style-reference-id"

export const publishStyleDraftWithRecovery = async (
	styleDraft: StyleDraft,
	options?: {
		projectStyleType?: ProjectStyleTypes
		onDuplicateNameResolved?: (result: PublishStyleDraftResult) => void | Promise<void>
		trackingViewName?: string
		dialogProps?: Partial<DialogProps>
	},
): Promise<PublishStyleDraftResult | null> => {
	try {
		return await publishStyleDraft(styleDraft)
	} catch (error) {
		if (isDuplicateStyleNameError(error) && options?.onDuplicateNameResolved) {
			const recoveryDraft: StyleDraft = { ...styleDraft }
			showDuplicateStyleNameRecoveryDialog({
				styleDraft: recoveryDraft,
				styleName: error.styleName,
				projectStyleType: options.projectStyleType ?? "My AI Style",
				trackingViewName: options.trackingViewName,
				onDuplicateNameResolved: options.onDuplicateNameResolved,
				dialogProps: options.dialogProps,
			})

			return null
		}

		throw error
	}
}

export const deleteStyleReference = async (imageId: string, styleId: string) => {
	await CatalogManager.deleteStyleReference(imageId)
	const references = await CatalogManager.fetchStyleReferences(styleId)
	store.set(styleReferencesAtom, references)
}

export const deleteStyleReferences = async (imageIds: string[], styleId: string) => {
	// TODO: implement batch deletion
	for (const id of imageIds) {
		await CatalogManager.deleteStyleReference(id)
	}

	const photos = store.get(photosAtom)
	let count = 0
	for (const id of imageIds) {
		const photo = photos[toLocalPhotoIdFromStyleReferenceImageId(id)]
		if (photo?.kind === "reference") {
			removeFromApproved(photo.id)
			++count
		}
	}
	if (count > 0) {
		const style = store.get(currentStyleAtom)
		if (style && style.styleId === styleId) {
			void updateAIStyle(style, "remove_reference")
		}
	}

	const references = await CatalogManager.fetchStyleReferences(styleId)
	store.set(styleReferencesAtom, references)
}

export const createStyleReferenceForLocalPhoto = (
	imageInfo: ImageInfoRecord,
	styleId: string,
	isImplicit: boolean,
) => {
	//Add reference to catalog
	const newReferenceRecord: StyleReferenceRecord = {
		imageId: toRemoteStyleReferenceImageId(imageInfo.imageId),
		styleId: styleId,
		imageInfo: imageInfo,
		referenceId: "", // to be filled by backend when uploaded
		isImplicit: isImplicit,
	}
	return newReferenceRecord
}

export const updateStyleReference = async (
	imageInfo: ImageInfoRecord,
	styleId: string,
	isImplicit: boolean,
) => {
	const updatedReference = await CatalogManager.updateStyleReference(
		toRemoteStyleReferenceImageId(imageInfo.imageId),
		{
			imageInfo: imageInfo,
			afterImageUrl: undefined, // reset afterImageUrl so it will be re-uploaded
			isImplicit: isImplicit,
		},
	)
	const references = await CatalogManager.fetchStyleReferences(styleId)
	store.set(styleReferencesAtom, references)
	return updatedReference
}

/**
 * Ensure LUTs referenced by a style are available in the project catalog for rendering.
 * Resolves each LUT from IndexedDB or downloads from cloud, then writes to the catalog
 * via MainLUTResolver.add() so the AI worker's resolver can find them.
 */
export async function fetchAndRegisterStyleLuts(
	lutUrls: Record<string, string> | undefined,
): Promise<void> {
	if (!lutUrls) return
	const entries = Object.entries(lutUrls)
	if (entries.length === 0) return

	await Promise.all(
		entries.map(async ([lutId, url]) => {
			try {
				// Resolve the LUT data: IndexedDB → cloud download.
				// Always call MainLUTResolver.add() because the catalog may not
				// have the LUT yet (e.g. style created before a project was open;
				// the main-thread LRU cache has it but the catalog and AI worker don't).
				let lookTable = await getStyleLut(lutId)
				if (!lookTable) {
					const response = await fetch(url)
					if (!response.ok) {
						console.warn(`[style-lut] Failed to fetch LUT ${lutId}: ${response.status}`)
						return
					}
					const buffer = await response.arrayBuffer()
					lookTable = deserializeLookTable(buffer)
					await storeStyleLut(lookTable)
				}
				await MainLUTResolver.add(lookTable)
			} catch (err) {
				console.warn(`[style-lut] Error fetching/registering LUT ${lutId}:`, err)
			}
		}),
	)
}

export async function useAndStoreRemoteStyleInProject(
	style: RemoteStyle,
): Promise<StyleInfoRecord> {
	const styleInfo = StyleService.responseToStyleInfo(
		style.response,
		style.prioritizeImageIds,
		style.globalBiases,
	)

	await fetchAndRegisterStyleLuts(styleInfo.lutUrls)

	// Sync updated referenceImages back to references to preserve style tuning changes.
	const references = style.references.map((ref) => {
		const updatedImageInfo = style.referenceImages[ref.imageInfo.imageId]
		return StyleService.responseToReference({
			...ref,
			imageInfo: updatedImageInfo ?? ref.imageInfo,
		})
	})
	await saveAsCurrentStyle(styleInfo, references)
	return styleInfo
}
